/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import {CustomerLogsClient, LogSearchOptions, EventsPageParameters} from '../CustomerLogsList'
import {useCallback} from 'react'
import {LogLevel, LogListItem} from '../LogsList'
import {useAsyncList} from '../hooks/useAsyncList'

const convertOptions = (options: LogSearchOptions): EventsPageParameters => {
  const convertedOptions: EventsPageParameters = {...options}
  if (options.level) {
    convertedOptions.levels = [options.level]
  }
  if (options.service) {
    convertedOptions.services = [options.service]
  }
  return convertedOptions
}

const defaultOptions: LogSearchOptions = {
  take: 50
}

const loadLogs = async (
  client: CustomerLogsClient,
  options: LogSearchOptions
): Promise<LogListItem[]> => {
  try {
    const result = await client.getEvents(convertOptions(options))
    return result.items
  } catch (error) {
    return []
  }
}

export const useCustomerLogsList = (
  client: CustomerLogsClient,
  initialOptions?: LogSearchOptions
) => {
  const load = useCallback((options: LogSearchOptions) => loadLogs(client, options), [client])

  const {items, options, setOptions, isLoading, hasMoreData, loadMoreData} = useAsyncList({
    defaultOptions: initialOptions ? {...defaultOptions, ...initialOptions} : defaultOptions,
    load
  })

  return {
    logs: items || ([] as LogListItem[]),
    options,
    setOptions,
    isLoadingLogs: isLoading,
    onLevelChange: (level?: LogLevel) =>
      setOptions({
        ...options,
        level
      }),
    onServiceChange: (service?: string) =>
      setOptions({
        ...options,
        service
      }),
    onQueryChange: (search?: string) =>
      setOptions({
        ...options,
        search
      }),
    onClearFiltersClick: () =>
      setOptions({
        ...options,
        level: undefined,
        service: undefined,
        search: undefined,
        createdAfter: undefined,
        createdBefore: undefined
      }),
    onLoadMore: async (_page: number) => loadMoreData(),
    onDateChange: (dateFilter: {beforeDate?: Date; afterDate?: Date}) => {
      setOptions({
        ...options,
        createdBefore: dateFilter.beforeDate,
        createdAfter: dateFilter.afterDate
      })
    },
    hasMoreData,
    hasNonEmptyFilter: (): boolean => {
      const {createdAfter, createdBefore, service, level, search} = options
      const filters = [createdAfter, createdBefore, service, level, search]
      return filters.some(filter => Boolean(filter))
    }
  }
}
